import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Sparkle, Robot, Spinner, Warning, Megaphone, ShoppingCart, CurrencyDollar,
  TrendUp, TrendDown, ArrowRight, MagnifyingGlass,
} from "@phosphor-icons/react";

/**
 * AlendraDashboardOverview — panel ejecutivo para el dashboard principal.
 * Muestra los headlines de los 5 módulos AI: cuentas de pendientes, top
 * acción/recomendación de cada uno, y alertas de pricing vs mercado.
 */
const fmtMXN = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;

export default function AlendraDashboardOverview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/alendra/dashboard")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="border border-zinc-200 rounded-lg p-8 bg-white text-center" data-testid="alendra-overview-loading">
        <Spinner size={20} className="animate-spin inline text-[#002FA7] mr-2"/>
        <span className="text-sm text-zinc-500">Cargando resumen de Alendra AI…</span>
      </div>
    );
  }
  if (!data) return null;

  const totalPending = (data.inventory?.pending || 0) + (data.marketing?.pending || 0) + (data.purchasing?.buy_more_pending || 0);
  const hasAnyAnalysis = data.inventory?.generated_at || data.marketing?.generated_at || data.purchasing?.generated_at;

  if (!hasAnyAnalysis) {
    return (
      <div className="border border-dashed border-[#002FA7]/30 rounded-lg p-6 bg-gradient-to-br from-[#002FA7]/5 to-purple-50" data-testid="alendra-overview-empty">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#002FA7] text-white flex items-center justify-center shrink-0">
            <Robot size={24} weight="fill"/>
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-base">Alendra AI está lista para analizar tu negocio</h3>
            <p className="text-sm text-zinc-600">Ve a <Link to="/insights" className="text-[#002FA7] font-semibold hover:underline">Insights</Link> y corre tu primer análisis de Inventario, Marketing y Compras.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-zinc-200 rounded-lg overflow-hidden bg-white" data-testid="alendra-overview">
      {/* Header */}
      <div className="px-5 py-3 bg-gradient-to-r from-[#002FA7] to-purple-700 text-white flex items-center gap-3">
        <Sparkle size={20} weight="fill" className="text-amber-300"/>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest opacity-80">Alendra AI Overview</div>
          <div className="font-bold text-lg leading-tight">
            {totalPending > 0
              ? <>Tienes <span className="text-amber-300 numeric">{totalPending}</span> acciones pendientes</>
              : "Estás al día con todas las recomendaciones"}
          </div>
        </div>
        <Link to="/insights" className="text-xs bg-white/20 hover:bg-white/30 transition px-3 py-1.5 rounded inline-flex items-center gap-1.5 font-semibold" data-testid="alendra-overview-go-insights">
          Ver detalle <ArrowRight size={12} weight="bold"/>
        </Link>
      </div>

      {/* Grid de módulos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zinc-100">
        <ModuleCard
          icon={<Robot size={18} weight="fill"/>}
          color="bg-[#002FA7]"
          title="Inventario"
          pending={data.inventory?.pending || 0}
          total={data.inventory?.total_actions || 0}
          generated_at={data.inventory?.generated_at}
          testId="overview-inventory"
        >
          {data.inventory?.alerts_count > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-amber-700 mb-1">
              <Warning size={12} weight="fill"/>
              <span>{data.inventory.alerts_count} alertas críticas</span>
            </div>
          )}
          {data.inventory?.top_action && (
            <div className="text-xs">
              <span className="font-semibold text-zinc-700">Próxima acción:</span>{" "}
              <span className="text-zinc-600">{data.inventory.top_action.action} → {data.inventory.top_action.product_name}</span>
            </div>
          )}
        </ModuleCard>

        <ModuleCard
          icon={<Megaphone size={18} weight="fill"/>}
          color="bg-pink-600"
          title="Marketing"
          pending={data.marketing?.pending || 0}
          total={data.marketing?.total_recommendations || 0}
          generated_at={data.marketing?.generated_at}
          testId="overview-marketing"
        >
          {data.marketing?.top_rec && (
            <div className="text-xs space-y-0.5">
              <div>
                <span className="font-semibold text-zinc-700">Siguiente contenido:</span>{" "}
                <span className="inline-block px-1.5 rounded text-[9px] font-bold uppercase bg-pink-100 text-pink-700">{data.marketing.top_rec.content_type?.replace("_", " ")}</span>{" "}
                <span className="text-zinc-600">{data.marketing.top_rec.product_name}</span>
              </div>
              {data.marketing.top_rec.hook && (
                <div className="italic text-zinc-500 text-[11px] line-clamp-2">🎬 {data.marketing.top_rec.hook}</div>
              )}
            </div>
          )}
        </ModuleCard>

        <ModuleCard
          icon={<ShoppingCart size={18} weight="fill"/>}
          color="bg-emerald-600"
          title="Compras"
          pending={data.purchasing?.buy_more_pending || 0}
          total={(data.purchasing?.buy_more_pending || 0) + 0}
          generated_at={data.purchasing?.generated_at}
          testId="overview-purchasing"
        >
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1 text-emerald-700">
              <TrendUp size={12} weight="fill"/>
              <span className="font-semibold numeric">{fmtMXN(data.purchasing?.revenue_potential_mxn)}</span>
              <span className="text-zinc-500">potencial</span>
            </div>
            {data.purchasing?.stop_buying_count > 0 && (
              <div className="flex items-center gap-1 text-rose-700">
                <TrendDown size={12} weight="fill"/>
                <span className="font-semibold numeric">{fmtMXN(data.purchasing?.stuck_value_mxn)}</span>
                <span className="text-zinc-500">parado</span>
              </div>
            )}
          </div>
          {data.purchasing?.top_buy && (
            <div className="text-xs mt-1">
              <span className="font-semibold text-zinc-700">Comprar:</span>{" "}
              <span className="text-zinc-600">×{data.purchasing.top_buy.suggested_qty} {data.purchasing.top_buy.product_name}</span>
            </div>
          )}
        </ModuleCard>
      </div>

      {/* Pricing alerts */}
      {(data.pricing?.alerts || []).length > 0 && (
        <div className="border-t border-zinc-100 p-4 bg-amber-50/40" data-testid="overview-pricing-alerts">
          <div className="flex items-center gap-2 mb-2">
            <CurrencyDollar size={18} weight="fill" className="text-amber-600"/>
            <span className="font-semibold text-sm">Alertas de precio vs mercado</span>
            <span className="text-[10px] text-zinc-500">— productos con desviación &ge; 10%</span>
          </div>
          <ul className="space-y-1.5">
            {data.pricing.alerts.map((a) => (
              <li key={`${a.product_id}-${a.generated_at}`} className="flex items-center gap-2 text-xs">
                <Link to="/products" className="font-semibold text-zinc-800 hover:text-[#002FA7] flex-1 truncate">{a.product_name}</Link>
                <span className="text-zinc-500 numeric">{fmtMXN(a.current_price)}</span>
                <ArrowRight size={10} className="text-zinc-400"/>
                <span className="font-bold text-emerald-700 numeric">{fmtMXN(a.recommended_price)}</span>
                <span className={`numeric font-bold text-[10px] px-1.5 py-0.5 rounded ${a.diff_pct > 0 ? "bg-amber-100 text-amber-700" : "bg-rose-100 text-rose-700"}`}>
                  {a.diff_pct > 0 ? "+" : ""}{a.diff_pct}%
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* SEO coverage */}
      <div className="border-t border-zinc-100 px-4 py-2.5 flex items-center gap-3 text-xs bg-zinc-50/50">
        <MagnifyingGlass size={14} className="text-zinc-500"/>
        <span className="text-zinc-600">Cobertura SEO:</span>
        <div className="flex-1 h-1.5 bg-zinc-200 rounded-full overflow-hidden max-w-[200px]">
          <div className="h-full bg-[#002FA7]" style={{ width: `${data.seo?.coverage_pct || 0}%` }}/>
        </div>
        <span className="font-semibold numeric text-zinc-800">{data.seo?.coverage_pct || 0}%</span>
        <span className="text-zinc-500">({data.seo?.products_with_seo} de {data.seo?.products_total} productos)</span>
      </div>
    </div>
  );
}

function ModuleCard({ icon, color, title, pending, total, generated_at, children, testId }) {
  const done = total - pending;
  const pct = total > 0 ? (done / total) * 100 : 0;
  return (
    <div className="p-4 space-y-2" data-testid={testId}>
      <div className="flex items-center gap-2">
        <div className={`w-7 h-7 rounded-md ${color} text-white flex items-center justify-center`}>{icon}</div>
        <span className="font-bold text-sm">{title}</span>
        {pending > 0 && (
          <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">
            {pending}
          </span>
        )}
      </div>
      {total > 0 && (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-1.5 rounded-full bg-zinc-100 overflow-hidden">
            <div className={`h-full ${pct === 100 ? "bg-emerald-500" : color}`} style={{ width: `${pct}%` }}/>
          </div>
          <span className="text-[10px] text-zinc-500 numeric">{done}/{total}</span>
        </div>
      )}
      <div className="space-y-1">{children}</div>
      {generated_at && (
        <div className="text-[10px] text-zinc-400 pt-1">
          Análisis: {new Date(generated_at).toLocaleDateString("es-MX", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}
        </div>
      )}
    </div>
  );
}
